import { Component, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { UserDetailsComponent } from './steps/user-details/user-details.component';
import { BrandDetails } from './steps/brand-details/brand-details';
import { RouteSelectComponent } from './steps/route-select/route-select';
import { SpeedySetupComponent } from './steps/speedy-setup/speedy-setup';
import { DepthSetupComponent } from './steps/depth-setup/depth-setup';
import { SlickOnboard } from './service/slick-onboard/slick-onboard.service';
import { LoadingInlineComponent } from '../../atoms/loading-inline/loading-inline.component';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { AlertService } from '../../atoms/alert/alert.service';
import { Router } from '@angular/router';
import { BriefResponseDto } from '../../common/general-types/briefs.dto';
import { RecommendedCreators } from './steps/recommended-creators/recommended-creators';
import { CongratsComponent } from './steps/congrats/congrats';
import { GaService } from '../../services/ga/ga';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { Location } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { AdConversionsService } from '@/app/services/analytics/ad-conversions.service';

@Component({
  selector: 'app-onboarding-v2',
  standalone: true,
  imports: [UserDetailsComponent, BrandDetails, RouteSelectComponent, 
    SpeedySetupComponent, DepthSetupComponent, LoadingInlineComponent, 
    RecommendedCreators, CongratsComponent],
  templateUrl: './onboarding-v2.component.html',
  styleUrl: './onboarding-v2.component.css'
})
export class OnboardingV2Component implements OnInit {

  showGeneralLoader = signal<boolean>(false);

  steps: { id: number, name: string, completed: boolean }[] = [
    { id: 1, name: 'User', completed: true },
    { id: 2, name: 'Brand', completed: false },
    { id: 3, name: 'Setup', completed: false },
    { id: 4, name: 'Creators', completed: false },
    { id: 5, name: 'Brief', completed: false },
    { id: 6, name: 'Publish', completed: false },
  ];

  currentStep = signal<number>(1);
  // currentStep = signal<number>(2);

  routePreference = signal<'speedy' | 'in-depth' | undefined>(undefined);

  /** Services */
  private slickOnboardService = inject(SlickOnboard);
  private currentUserService = inject(CurrentUserService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  private gaService = inject(GaService);
  private adConversions = inject(AdConversionsService);
  private firebaseService = inject(FirebaseService);
  private location = inject(Location);
  private document = inject(DOCUMENT);
  /** Services - END */


  private readonly bounceInDepthPastCreatorsStep = effect(() => {
    const step = this.currentStep();
    const preference = this.routePreference();
    if (step === 5 && preference === 'in-depth') {
      untracked(() => this.currentStep.set(6));
    }
  });

  ngOnInit(): void {
    this.getHubspotUserTokenWithDelay();
  }

  nextStep(event: boolean, step: number) {
    console.log('Onboarding v2 nextStep: ', event, step);


    this.steps[step - 1].completed = event;
    this.currentStep.set(step + 1);
  }

  async signUpAndNext(event: boolean, step: number) {
    console.log('signUpAndNext(): ', event, step);

    this.showGeneralLoader.set(true);
    console.log('😳😳😳 Create Accounts.....');

    try {
      const signUpResult: any = await this.slickOnboardService.registerBrandUser();
      console.log('😳😳😳 Sign Up Result: ', signUpResult);

      if (signUpResult !== false) {
        this.currentUserService.setShoutUser(signUpResult.user);
        this.currentUserService.setFocussedBrand(signUpResult.business, this.firebaseService.accessToken);
        void this.adConversions.saveFirstTouch('brand', signUpResult.business?.id);
        this.adConversions.trackSignup('brand', signUpResult.business?.id);
        this.steps[step - 1].completed = event;
        this.currentStep.set(step + 1);
        this.showGeneralLoader.set(false);
        const analyticsUserId = signUpResult.user?.firebaseUid || signUpResult.user?.id;
        this.gaService.identifyAnalyticsUser(analyticsUserId, {
          email: signUpResult.user?.email,
          name: `${signUpResult.user?.firstName} ${signUpResult.user?.lastName}`,
        });
        this.gaService.trackOnboardingEvent('user_signed_up', {
          firebase_uid: analyticsUserId,
          business_name: signUpResult.business?.name,
        });
        this.gaService.trackOnboardingEvent('brand_account_created', {
          business_id: signUpResult.business?.id,
          firebase_uid: analyticsUserId,
        });
      } else {
        this.showGeneralLoader.set(false);
        this.alertService.alert({
          title: 'Login?',
          message: 'Looks like you already have an account with this email address. Would you like to login?',
          icon: 'info',
          confirmText: 'Login',
          cancelText: 'Cancel',
          type: 'confirm',
        }).then((result: any) => {
          if (result) {
            this.router.navigate(['/login']);
          } else {
            this.currentStep.set(step - 1);
          }
        });
      }

    } catch (error) {
      console.error('Failed to create brand user: ', error);
      this.showGeneralLoader.set(false);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'We could not finish creating your account. Please try again.';
      void this.alertService.alert({
        title: 'Something went wrong',
        message,
        icon: 'error',
        type: 'alert',
        confirmText: 'Okay',
        cancelText: 'Close',
      });
    }
  }

  routeSelected(event: any, step: number) {
    console.log('Onboarding v2 routeSelected: ', event, step);
    this.steps[step - 1].completed = true;
    this.routePreference.set(event);

    if (event === 'in-depth') {
      this.currentUserService.onboardingComplete.set(true);
      this.currentUserService.checkBrandComplete();
      this.router.navigate(['/dashboard']);
      return;
    }

    this.currentStep.set(step + 1);
  }

  briefCreated(event: BriefResponseDto, step: number) {
    console.log('Onboarding v2 briefCreated: ', event);
    this.steps[step - 1].completed = true;
    this.currentStep.set(step + 1);
  }

  depthSetupComplete(event: boolean, step: number) {
    console.log('Onboarding v2 depthSetupComplete: ', event, step);
    this.steps[step - 1].completed = event;
    this.steps[step].completed = true;
    this.currentStep.set(step + 2);
  }

  creatorsInvited(event: boolean, step: number) {
    console.log('Onboarding v2 creatorsInvited: ', event);
    this.steps[step - 1].completed = true;
    this.currentStep.set(step + 1);
  }

  getStepBarClass(step: { id: number, name: string, completed: boolean }): string {
    if (step.completed || step.id === this.currentStep()) {
      return 'bg-linear-to-r from-primary to-brand-studio';
    }
    return 'bg-zinc-200 dark:bg-zinc-800';
  }

  goBack() {
    if (this.currentStep() === 2) {
      return;
    }
    if (this.currentStep() > 1) {
      this.currentStep.set(this.currentStep() - 1);
      return;
    }
    this.location.back();
  }

  goToStep(stepId: number) {
    if (this.currentStep() === 2 && stepId < 2) {
      return;
    }
    this.currentStep.set(stepId);
  }

  getHubspotUserTokenWithDelay() {
    setTimeout(() => {
      this.slickOnboardService.hubspotUserToken = this.getCookie('hubspotutk');
      console.log('HubSpot User Token:', this.slickOnboardService.hubspotUserToken);
      if (!this.slickOnboardService.hubspotUserToken) {
        console.log('HubSpot User Token is not available yet. Retrying...');
      }
    }, 500); // 500ms delay
  }

  // A helper function to parse cookies from document.cookie
  private getCookie(name: string): string | undefined {
    const cookies = this.document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1, cookie.length);
      }
    }
    return undefined;
  }
}
